// src/pages/TicketDetails.js

import React, { useState, useEffect, useMemo, useRef, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    FiEdit, FiTrash2, FiArrowLeft, FiMapPin, FiExternalLink, FiSave,
    FiXCircle, FiUserPlus, FiSearch, FiX, FiMessageSquare, FiPlus, FiEdit2,
    FiPackage, FiCheck, FiTool, FiCopy, FiCalendar, FiBriefcase, FiLayers, FiFileText,
    FiUploadCloud, FiDownload, FiImage, FiEye, FiLogIn, FiLogOut, FiPaperclip, 
    FiUser, FiHome, FiClock, FiShare2, FiUsers, FiUserX, FiFilm,
    FiInbox, FiPlayCircle, FiPauseCircle, FiCheckCircle, FiChevronDown, FiChevronUp,
    FiAlertTriangle, FiChevronsUp, FiMinus, FiChevronsDown, FiMoreVertical,
    FiPhone, FiMail 
} from 'react-icons/fi';
import { momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';

import AssignTechnicianModal from '../components/AssignTechnicianModal';
import ConfirmationModal from '../components/ConfirmationModal';
import { AuthContext } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';
import CheckboxGroup from '../components/CheckboxGroup';
import AssignContactsModal from '../components/AssignContactsModal';
import CheckOutModal from '../components/CheckOutModal';
import AddNoteModal from '../components/AddNoteModal';
import InventoryHistoryModal from '../components/InventoryHistoryModal'; 
import UnitHistoryModal from '../components/UnitHistoryModal';
import { useApi } from '../hooks/useApi';
import useLongPress from '../hooks/useLongPress'; 
import TicketQueue from '../components/TicketQueue';
import RadialStatusMenu from '../components/RadialStatusMenu';
import { useIsMobile } from '../hooks/useIsMobile';

const localizer = momentLocalizer(moment);

// --- HELPER FUNCTIONS ---
const isTicketOverdue = (ticket, appointments) => {
    if (!ticket || ['Closed', 'Pending', 'Waiting On Client', 'Waiting On Parts'].includes(ticket.status)) { return false; }
    const now = new Date();
    const twoDaysInMs = 2 * 24 * 60 * 60 * 1000;
    const futureAppointments = (appointments || [])
        .filter(app => {
            const ticketId = typeof app.ticketId === 'object' ? app.ticketId._id : app.ticketId;
            return ticketId === ticket._id && new Date(app.startTime) > now;
        })
        .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
    
    const soonestFutureAppointment = futureAppointments[0];
    if (!soonestFutureAppointment) {
        const createdAt = new Date(ticket.createdAt);
        if (now.getTime() - createdAt.getTime() > twoDaysInMs) {
            return true;
        }
    }
    
    const allAppointmentsForTicket = (appointments || [])
        .filter(app => {
            const ticketId = typeof app.ticketId === 'object' ? app.ticketId._id : app.ticketId;
            return ticketId === ticket._id;
        })
        .sort((a, b) => new Date(b.startTime) - new Date(a.startTime));
    if (allAppointmentsForTicket.length > 0) {
        const latestAppointmentTime = new Date(allAppointmentsForTicket[0].startTime);
        const lastUpdatedTime = new Date(ticket.updatedAt || ticket.createdAt);
        if (now.getTime() - latestAppointmentTime.getTime() > twoDaysInMs && lastUpdatedTime.getTime() < latestAppointmentTime.getTime()) {
            return true;
        }
    }
    return false;
};

const formatDuration = (totalSeconds) => {
    if (totalSeconds < 0) totalSeconds = 0;
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

function getAssignedObjects(idArray, sourceArray) {
    if (!idArray || !sourceArray) return [];
    return sourceArray.filter(obj => idArray.includes(obj._id));
}

// --- SUB-COMPONENTS ---

const TicketTimeTracker = ({ ticket }) => {
    const totalPastSeconds = useMemo(() => {
        if (!ticket?.timeLogs) return 0;
        return ticket.timeLogs.reduce((acc, log) => acc + (log.duration || 0), 0);
    }, [ticket?.timeLogs]);

    const [liveSeconds, setLiveSeconds] = useState(0);

    useEffect(() => {
        if (!ticket?.activeCheckIns || ticket.activeCheckIns.length === 0) {
            setLiveSeconds(0);
            return;
        }
        const interval = setInterval(() => {
            const totalElapsed = ticket.activeCheckIns.reduce((total, checkIn) => {
                return total + (Date.now() - new Date(checkIn.checkInTime).getTime()) / 1000;
            }, 0);
            setLiveSeconds(totalElapsed);
        }, 1000);
        const initialTotalElapsed = ticket.activeCheckIns.reduce((total, checkIn) => {
            return total + (Date.now() - new Date(checkIn.checkInTime).getTime()) / 1000;
        }, 0);
        setLiveSeconds(initialTotalElapsed);
        return () => clearInterval(interval);
    }, [ticket?.activeCheckIns]);

    const totalSeconds = totalPastSeconds + liveSeconds;
    if (totalSeconds === 0) return null;
    return (
        <div className="time-tracking-badge" title="Total Time Logged on Ticket">
            <FiClock size={12} />
            <span>{formatDuration(totalSeconds)}</span>
        </div>
    );
};

const TechnicianTimeLog = ({ ticket, technicianId }) => {
    const pastSecondsForTech = useMemo(() => {
        if (!ticket?.timeLogs) return 0;
        return ticket.timeLogs
            .filter(log => log.technicianId.toString() === technicianId.toString())
            .reduce((acc, log) => acc + (log.duration || 0), 0);
    }, [ticket?.timeLogs, technicianId]);

    const [liveSecondsForTech, setLiveSecondsForTech] = useState(0);

    useEffect(() => {
        const activeCheckIn = ticket?.activeCheckIns?.find(
            ci => ci.technicianId.toString() === technicianId.toString()
        );
        if (activeCheckIn) {
            const interval = setInterval(() => {
                const elapsed = (Date.now() - new Date(activeCheckIn.checkInTime).getTime()) / 1000;
                setLiveSecondsForTech(elapsed);
            }, 1000);
            const initialElapsed = (Date.now() - new Date(activeCheckIn.checkInTime).getTime()) / 1000;
            setLiveSecondsForTech(initialElapsed);
            return () => clearInterval(interval);
        } else {
            setLiveSecondsForTech(0);
        }
    }, [ticket?.activeCheckIns, technicianId]);

    const totalSecondsForTech = pastSecondsForTech + liveSecondsForTech;
    if (totalSecondsForTech === 0) return null;
    return (
        <div className="time-tracking-badge" title={`Time logged by this technician: ${formatDuration(totalSecondsForTech)}`} style={{ marginLeft: '0.5rem' }}>
            <FiClock size={12} />
            <span>{formatDuration(totalSecondsForTech)}</span>
        </div>
    );
};

const DocumentViewerModal = ({ isOpen, onClose, document, content }) => {
    useEffect(() => {
        const handleKeyDown = (e) => { if (e.key === 'Escape' && isOpen) { e.preventDefault(); onClose(); } };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen || !document) return null;
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-content-large" onClick={e => e.stopPropagation()}>
                <div className="modal-header"><h3>{document.originalName}</h3><button onClick={onClose} className="modal-close-btn">×</button></div>
                <div className="modal-body" style={{ background: '#f8f9fa' }}><pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0, height: '100%', overflowY: 'auto' }}><code>{content}</code></pre></div>
                <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Close</button></div>
            </div>
        </div>
    );
};

const ImageViewerModal = ({ isOpen, onClose, imageUrl, docName }) => {
    useEffect(() => { return () => { if (imageUrl) { URL.revokeObjectURL(imageUrl); } }; }, [imageUrl]);
    
    useEffect(() => {
        const handleKeyDown = (e) => { if (e.key === 'Escape' && isOpen) { e.preventDefault(); onClose(); } };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen || !imageUrl) return null;
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="image-viewer-modal-content" onClick={e => e.stopPropagation()}>
                <button onClick={onClose} className="image-viewer-close-btn" title="Close"><FiX size={20} /></button>
                <img src={imageUrl} alt={docName} />
            </div>
        </div>
    );
};

const VideoViewerModal = ({ isOpen, onClose, videoUrl, docName }) => {
    useEffect(() => { return () => { if (videoUrl) { URL.revokeObjectURL(videoUrl); } }; }, [videoUrl]);
    
    useEffect(() => {
        const handleKeyDown = (e) => { if (e.key === 'Escape' && isOpen) { e.preventDefault(); onClose(); } };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen || !videoUrl) return null;
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="image-viewer-modal-content" onClick={e => e.stopPropagation()}>
                <button onClick={onClose} className="image-viewer-close-btn" title="Close"><FiX size={20} /></button>
                <video controls autoPlay name="media">
                    <source src={videoUrl} />
                    Your browser does not support the video tag.
                </video>
            </div>
        </div>
    );
};

const InfoTagWithTooltip = ({ name, tooltipItems, onCopy, position = 'bottom' }) => {
    const [isTooltipVisible, setIsTooltipVisible] = useState(false);
    const hideTimer = useRef(null);

    const handleMouseEnter = () => { 
        clearTimeout(hideTimer.current); 
        setIsTooltipVisible(true); 
    };

    const handleMouseLeave = () => { 
        hideTimer.current = setTimeout(() => { 
            setIsTooltipVisible(false); 
        }, 300); 
    };

    const handleCopyClick = (e, item) => { 
        e.stopPropagation(); 
        navigator.clipboard.writeText(item.value); 
        if (onCopy) { onCopy(item.label); } 
    };

    return (
        <span 
            className="detail-tag" 
            onMouseEnter={handleMouseEnter} 
            onMouseLeave={handleMouseLeave}
            style={{ position: 'relative', cursor: 'pointer' }}
        >
            {name}
            <div className={`tooltip-wrapper ${isTooltipVisible ? 'visible' : ''} position-${position}`}>
                <div className="tooltip-content">
                    <div style={{ fontWeight: '600', borderBottom: '1px solid var(--gray-200)', paddingBottom: '0.5rem', marginBottom: '0.25rem', color: 'var(--gray-800)' }}>
                        {name}
                    </div>
                    {tooltipItems.map(item => (
                        <div key={item.label} className="tooltip-item" onClick={(e) => handleCopyClick(e, item)}>
                            {item.label === 'Email' ? <FiMail size={16} className="tooltip-item-icon" /> : <FiPhone size={16} className="tooltip-item-icon" />}
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <small style={{ fontSize: '0.65rem', color: 'var(--gray-500)', textTransform: 'uppercase', fontWeight: 'bold' }}>{item.label}</small>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '160px' }}>{item.value}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </span>
    );
};

const ClipboardNotification = ({ message, isVisible }) => { if (!isVisible) return null; return ( <div className={`clipboard-notification ${isVisible ? 'visible' : ''}`}>{message}</div> ); };
const formatPhoneNumber = (value) => { if (!value) return ''; const phoneNumber = value.replace(/\D/g, ''); const phoneNumberLength = phoneNumber.length; if (phoneNumberLength < 4) return phoneNumber;
if (phoneNumberLength < 7) { return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`; } return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`; };

const AssignDepartmentsModal = ({ isOpen, onClose, onSave, allDepartments, currentDepartments = [] }) => {
    const [selectedDepts, setSelectedDepts] = useState(currentDepartments);
    useEffect(() => { if (isOpen) { setSelectedDepts(currentDepartments); } }, [isOpen, currentDepartments]);
    
    useEffect(() => {
        const handleKeyDown = (e) => { if (e.key === 'Escape' && isOpen) { e.preventDefault(); onClose(); } };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen) return null;
    const handleSave = () => { onSave(selectedDepts); onClose(); };
    return (
        <div className="modal-overlay">
            <div className="modal-content modal-content-slim">
                <div className="modal-header"><h3 className="modal-title">Assign Departments</h3><button onClick={onClose} className="modal-close-btn"><FiX /></button></div>
                <div className="modal-body">
                    <CheckboxGroup title="Select the departments this ticket belongs to:" options={allDepartments.map(d => d.name)} selected={selectedDepts} onChange={(newSelected) => setSelectedDepts(newSelected)} />
                </div>
                <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="button" className="btn btn-primary" onClick={handleSave}>Save Departments</button></div>
            </div>
        </div>
    );
};

const AssignClientModal = ({ isOpen, onClose, onSave, communities, currentClientName }) => {
    const [selectedClient, setSelectedClient] = useState(currentClientName || ''); const [searchTerm, setSearchTerm] = useState('');
    useEffect(() => { if (isOpen) { setSelectedClient(currentClientName || ''); setSearchTerm(''); } }, [isOpen, currentClientName]);
    
    useEffect(() => {
        const handleKeyDown = (e) => { if (e.key === 'Escape' && isOpen) { e.preventDefault(); onClose(); } };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);
    if (!isOpen) return null;
    const filteredCommunities = communities.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()));
    const handleSave = () => { if (selectedClient !== currentClientName) { onSave(selectedClient); } onClose(); };
    return ( <div className="modal-overlay" onClick={onClose}><div className="modal-content" onClick={e => e.stopPropagation()}><div className="modal-header"><h3 className="modal-title">Assign Client</h3><button onClick={onClose} className="modal-close-btn"><FiX /></button></div><div className="modal-body"><div className="search-input-wrapper modal-search-wrapper"><FiSearch className="search-icon" /><input type="search" placeholder="Search for a community..." className="search-input" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} autoFocus /></div><div className="items-list" style={{ maxHeight: '300px', overflowY: 'auto', marginTop: '1rem' }}>{filteredCommunities.length > 0 ? ( filteredCommunities.map(community => ( <div key={community._id} className={`list-item-selectable ${selectedClient === community.name ? 'selected' : ''}`} onClick={() => setSelectedClient(community.name)}> {community.name} </div> )) ) : ( <p className="no-tasks-message">No communities found.</p> )}</div></div><div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="button" className="btn btn-primary" onClick={handleSave} disabled={selectedClient === currentClientName}>Save Changes</button></div></div></div> );
};

const AssignTicketGroupModal = ({ isOpen, onClose, onSave, ticketGroups, currentTicketGroupId }) => {
    const [selectedGroupId, setSelectedGroupId] = useState(currentTicketGroupId || '');
    useEffect(() => { if (isOpen) { setSelectedGroupId(currentTicketGroupId || ''); } }, [isOpen, currentTicketGroupId]);
    
    useEffect(() => {
        const handleKeyDown = (e) => { if (e.key === 'Escape' && isOpen) { e.preventDefault(); onClose(); } };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);
    if (!isOpen) return null;
    const handleSave = () => { if (selectedGroupId !== currentTicketGroupId) { onSave(selectedGroupId); } onClose(); };
    return ( <div className="modal-overlay" onClick={onClose}><div className="modal-content modal-content-slim" onClick={e => e.stopPropagation()}><div className="modal-header"><h3 className="modal-title">Assign Ticket Group</h3><button onClick={onClose} className="modal-close-btn"><FiX /></button></div><div className="modal-body"><div className="items-list" style={{ maxHeight: '300px', overflowY: 'auto' }}><div className={`list-item-selectable ${!selectedGroupId ? 'selected' : ''}`} onClick={() => setSelectedGroupId('')}>-- Unassigned --</div>{ticketGroups.map(group => ( <div key={group._id} className={`list-item-selectable ${selectedGroupId === group._id ? 'selected' : ''}`} onClick={() => setSelectedGroupId(group._id)}>{group.name}</div> ))}</div></div><div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="button" className="btn btn-primary" onClick={handleSave} disabled={selectedGroupId === currentTicketGroupId}>Save Changes</button></div></div></div> );
};

const TicketNotes = ({ ticketId, notes, onAddNoteClick, onUpdateNote, onDeleteNote, apiCallForBlob, canEdit }) => {
    const [editingNoteId, setEditingNoteId] = useState(null); 
    const [editedText, setEditedText] = useState(''); 
    const [isExpanded, setIsExpanded] = useState(false);
    const [noteSearchTerm, setNoteSearchTerm] = useState('');

    const notesEndRef = useRef(null); 
    const editTextAreaRef = useRef(null);
    const { showAlert } = useModal(); 
    const { user } = useContext(AuthContext);

    const [isVideoViewerOpen, setIsVideoViewerOpen] = useState(false);
    const [isImageViewerOpen, setIsImageViewerOpen] = useState(false); 
    const [isTextViewerOpen, setIsTextViewerOpen] = useState(false); 
    const [viewingDocument, setViewingDocument] = useState(null); 
    const [viewerContent, setViewerContent] = useState('');
    
    const handleEditClick = (note) => { setEditingNoteId(note._id); setEditedText(note.text); };
    const handleCancelEdit = () => { setEditingNoteId(null); setEditedText(''); };
    const handleSaveEdit = (e) => { e.preventDefault(); if (!editedText.trim()) return; onUpdateNote(ticketId, editingNoteId, { text: editedText }); handleCancelEdit(); };
    const handleDeleteClick = (noteId) => { onDeleteNote(ticketId, noteId); };
    
    useEffect(() => { if (editingNoteId) { editTextAreaRef.current?.focus(); } }, [editingNoteId]);
    
    const isViewableImage = (fileType) => fileType.startsWith('image/');
    const isViewableVideo = (fileType) => fileType.startsWith('video/');
    const isViewableTextFile = (fileType) => ['text/plain', 'text/csv', 'text/html', 'text/xml', 'application/json', 'application/javascript'].some(type => fileType.startsWith(type));
    
    const handleView = async (doc) => { 
        const blob = await apiCallForBlob(`documents/stream/${doc._id}`); 
        if (!blob) { showAlert('Could not load file for preview.', 'Load Error'); return; } 
        
        if (isViewableImage(doc.fileType)) { 
            const url = URL.createObjectURL(blob); 
            setViewerContent(url); 
            setViewingDocument(doc); 
            setIsImageViewerOpen(true);
        } 
        else if (isViewableVideo(doc.fileType)) {
            const url = URL.createObjectURL(blob); 
            setViewerContent(url); 
            setViewingDocument(doc); 
            setIsVideoViewerOpen(true);
        }
        else if (isViewableTextFile(doc.fileType)) { 
            const text = await blob.text(); 
            setViewerContent(text); 
            setViewingDocument(doc); 
            setIsTextViewerOpen(true); 
        } 
    };
    
    const handleDownload = async (doc) => { const blob = await apiCallForBlob(`documents/stream/${doc._id}`); if (blob) { const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = doc.originalName; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url); } else { showAlert('Could not download file.', 'Download Error'); } };
    
    const filteredNotes = useMemo(() => {
        if (!noteSearchTerm.trim()) return notes || [];
        const query = noteSearchTerm.toLowerCase();
        return (notes || []).filter(note => 
            (note.text && note.text.toLowerCase().includes(query)) ||
            (note.author && note.author.toLowerCase().includes(query))
        );
    }, [notes, noteSearchTerm]);
    
    const displayedNotes = isExpanded ? filteredNotes : (filteredNotes || []).slice(0, 5);
    const showExpandButton = !noteSearchTerm && (notes || []).length > 5;

    return (
        <>
            <ImageViewerModal isOpen={isImageViewerOpen} onClose={() => setIsImageViewerOpen(false)} imageUrl={viewerContent} docName={viewingDocument?.originalName} />
            <VideoViewerModal isOpen={isVideoViewerOpen} onClose={() => setIsVideoViewerOpen(false)} videoUrl={viewerContent} docName={viewingDocument?.originalName} />
            <DocumentViewerModal isOpen={isTextViewerOpen} onClose={() => setIsTextViewerOpen(false)} document={viewingDocument} content={viewerContent}/>
        
            <div className="card">
                <div className="card-header" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <h3 className="card-title">
                            <FiMessageSquare style={{ marginRight: '0.5rem', verticalAlign: 'bottom' }} />Ticket Notes
                        </h3>
                        <button className="btn btn-primary btn-sm" onClick={onAddNoteClick}>
                            <FiPlus className="btn-icon" /> Add Note
                        </button>
                    </div>

                    <div className="search-input-wrapper" style={{ width: '100%' }}>
                        <FiSearch className="search-icon" />
                        <input 
                            type="text" 
                            className="search-input" 
                            placeholder="Search notes or authors..." 
                            value={noteSearchTerm}
                            onChange={(e) => setNoteSearchTerm(e.target.value)}
                        />
                        {noteSearchTerm && (
                            <button 
                                onClick={() => setNoteSearchTerm('')} 
                                className="btn-icon-only" 
                                style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)' }}
                            >
                                <FiX size={14} />
                            </button>
                        )}
                    </div>
                </div>
                
                <div className="card-body" style={{ padding: '0', display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                    
                    {showExpandButton && (
                        <button 
                            className="btn btn-link btn-block" 
                            onClick={() => setIsExpanded(!isExpanded)}
                            style={{ padding: '1rem', color: 'var(--blue-600)', fontWeight: '500', textDecoration: 'none', borderBottom: '1px solid var(--gray-200)' }}
                        >
                            {isExpanded ? (
                                <>Show Less <FiChevronUp style={{marginLeft:'4px'}}/></>
                            ) : (
                                <>Show {notes.length - 5} Older Notes <FiChevronDown style={{marginLeft:'4px'}}/></>
                            )}
                        </button>
                    )}

                    <div className="notes-list">
                        {displayedNotes && displayedNotes.length > 0 ? ( 
                            displayedNotes.map((note, index) => {
                                const isAuthor = user && note.authorId === user.id;
                                return ( 
                                    <div key={note._id || index} className="note-item">
                                        {editingNoteId === note._id ? ( 
                                            <form onSubmit={handleSaveEdit} className="edit-note-form">
                                                <textarea ref={editTextAreaRef} value={editedText} onChange={(e) => setEditedText(e.target.value)} className="form-textarea" rows="3" />
                                                <div className="edit-note-actions">
                                                    <button type="button" className="btn btn-secondary btn-sm" onClick={handleCancelEdit}>Cancel</button>
                                                    <button type="submit" className="btn btn-primary" disabled={!editedText.trim() || editedText === note.text}>Save</button>
                                                </div>
                                            </form> 
                                        ) : ( 
                                            <>
                                                <div className="note-header">
                                                    <span className="note-author">{note.author}</span>
                                                    <span className="note-timestamp">
                                                        {note.isUploading ? 'Uploading...' : new Date(note.createdAt).toLocaleString()}
                                                    </span>
                                                </div>

                                                {note.isUploading && (
                                                    <div className="note-upload-progress-container">
                                                        <div className="note-upload-bar-track">
                                                            <div 
                                                                className={`note-upload-bar-fill ${note.uploadProgress === undefined ? 'indeterminate' : ''}`}
                                                                style={{ width: note.uploadProgress !== undefined ? `${note.uploadProgress}%` : '100%' }}
                                                            ></div>
                                                        </div>
                                                        <span className="note-upload-text">
                                                            {note.uploadProgress !== undefined 
                                                                ? `${note.uploadProgress}% Uploaded` 
                                                                : 'Processing...'}
                                                        </span>
                                                    </div>
                                                )}

                                                <p className="note-body" style={note.isError ? { color: 'var(--red-600)' } : {}}>
                                                    {note.text}
                                                </p>

                                                {note.attachments && note.attachments.length > 0 && (
                                                    <div className="note-attachments-container" style={note.isUploading ? { opacity: 0.7 } : {}}>
                                                        {note.attachments.map(doc => { 
                                                            if (isViewableImage(doc.fileType)) { 
                                                                return ( <img key={doc._id} src={doc.filePath} alt={doc.originalName} className="note-attachment-image" onClick={() => handleView(doc)} title={`Click to view full size: ${doc.originalName}`} /> ); 
                                                            } else { 
                                                                const Icon = isViewableVideo(doc.fileType) ? FiFilm : FiPaperclip;
                                                                return (
                                                                    <div key={doc._id} className="note-attachment-file">
                                                                        <Icon size={14} />
                                                                        <span className="file-name">{doc.originalName}</span>
                                                                        <div className="file-actions">
                                                                            {(isViewableTextFile(doc.fileType) || isViewableVideo(doc.fileType)) && <button onClick={() => handleView(doc)} title="View"><FiEye size={12}/></button>}
                                                                            <button onClick={() => handleDownload(doc)} title="Download"><FiDownload size={12}/></button>
                                                                        </div>
                                                                    </div> 
                                                                ); 
                                                            } 
                                                        })}
                                                    </div> 
                                                )}
                                                
                                                {note._id && (canEdit || isAuthor) && !note.isUploading && ( 
                                                    <div className="note-actions">
                                                        <button onClick={() => handleEditClick(note)} title="Edit Note" className="btn-icon-only"><FiEdit2 size={14} /></button>
                                                        <button onClick={() => handleDeleteClick(note._id)} title="Delete Note" className="btn-icon-only btn-icon-danger"><FiTrash2 size={14} /></button>
                                                    </div> 
                                                )}
                                            </> 
                                        )}
                                    </div> 
                                ) 
                            }) 
                        ) : ( 
                            <p className="no-tasks-message" style={{ padding: '1rem' }}>
                                {noteSearchTerm ? `No results found for "${noteSearchTerm}"` : "No notes for this ticket yet."}
                            </p> 
                        )}
                        <div ref={notesEndRef} />
                    </div>
                </div>
            </div>
        </>
    );
};

const AddInventoryModal = ({ isOpen, onClose, truckInventory, onAddItem }) => {
    const [selectedItemId, setSelectedItemId] = useState(''); 
    const [quantity, setQuantity] = useState(1); 
    const [searchTerm, setSearchTerm] = useState(''); 
    const { showAlert } = useModal();

    useEffect(() => { 
        if (isOpen) { 
            setSelectedItemId(''); 
            setQuantity(1); 
            setSearchTerm('');
        } 
    }, [isOpen]);
    
    useEffect(() => {
        const handleKeyDown = (e) => { if (e.key === 'Escape' && isOpen) { e.preventDefault(); onClose(); } };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);
    if (!isOpen) return null;

    const filteredInventory = truckInventory.filter(item => 
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
        (item.sku && item.sku.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const handleSubmit = (e) => { 
        e.preventDefault(); 
        const itemToAdd = truckInventory.find(i => i._id === selectedItemId); 
        if (!itemToAdd || quantity < 1) return; 
        
        if (quantity > itemToAdd.quantity) { 
            showAlert(`Cannot add more than available stock (${itemToAdd.quantity}).`, 'Stock Limit');
            return; 
        } 
        onAddItem({ inventoryId: selectedItemId, quantityUsed: Number(quantity) }); 
        onClose(); 
    };

    const selectedItem = truckInventory.find(i => i._id === selectedItemId);

    return ( 
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-content-slim" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Add Item from Truck</h3>
                    <button onClick={onClose} className="modal-close-btn"><FiX /></button>
                </div>
                <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', height: '60vh' }}>
                    <div className="search-input-wrapper modal-search-wrapper" style={{ flexShrink: 0, marginBottom: '1rem' }}>
                        <FiSearch className="search-icon" />
                        <input 
                            type="search" 
                             placeholder="Search truck inventory..." 
                             className="search-input" 
                             value={searchTerm} 
                            onChange={(e) => setSearchTerm(e.target.value)} 
                            autoFocus 
                         />
                    </div>

                    <div className="items-list" style={{ flexGrow: 1, overflowY: 'auto', border: '1px solid var(--gray-200)', borderRadius: '8px' }}>
                        {filteredInventory.length > 0 ? (
                            filteredInventory.map(item => {
                                 const isSelected = selectedItemId === item._id;
                                 const isOutOfStock = item.quantity === 0;

                                 return (
                                     <div 
                                         key={item._id} 
                                         className={`list-item-row clickable-row ${isSelected ? 'selected' : ''}`}
                                         style={{ 
                                            opacity: isOutOfStock ? 0.5 : 1, 
                                            pointerEvents: isOutOfStock ? 'none' : 'auto',
                                            cursor: isOutOfStock ? 'not-allowed' : 'pointer'
                                         }}
                                         onClick={() => !isOutOfStock && setSelectedItemId(item._id)}
                                     >
                                         <div className="list-view-image-thumbnail-wrapper" style={{ marginRight: '1rem' }}>
                                             {item.imageUrl ? (
                                                 <img src={item.imageUrl} alt={item.name} className="list-view-image-thumbnail" />
                                             ) : (
                                                <FiPackage size={20} color="var(--gray-400)" />
                                             )}
                                         </div>

                                         <div className="item-info">
                                             <span className="item-title">{item.name}</span>
                                             <span className="item-subtitle">
                                                 SKU: {item.sku} • In Truck: <strong>{item.quantity}</strong>
                                             </span>
                                         </div>

                                         <div className="item-actions">
                                             {isSelected && <FiCheck size={20} color="var(--blue-600)" />}
                                         </div>
                                     </div>
                                 );
                            })
                        ) : (
                             <div className="no-tasks-message" style={{ padding: '2rem' }}>
                                <p>No items found.</p>
                             </div>
                          )}
                    </div>
                </div>

                <form id="add-inventory-form" onSubmit={handleSubmit} className="modal-footer" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <label htmlFor="quantity" style={{ fontWeight: '600', fontSize: '0.9rem' }}>Qty:</label>
                        <input 
                            id="quantity" 
                            type="number" 
                            className="form-input" 
                            style={{ width: '80px' }}
                            value={quantity} 
                            onChange={e => setQuantity(Number(e.target.value))} 
                            min="1" 
                            max={selectedItem ? selectedItem.quantity : 1} 
                            required 
                            disabled={!selectedItem} 
                        />
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
                        <button type="submit" className="btn btn-primary" disabled={!selectedItem}>
                            Add Item
                        </button>
                    </div>
                </form>
            </div>
        </div> 
    );
};

const AddSubTicketModal = ({ isOpen, onClose, technicians, onSave }) => {
    const initialState = { subject: '', body: '', priority: 'Medium', assignedTechnicianIds: [] }; 
    const [subTicketData, setSubTicketData] = useState(initialState); 
    const [isAssignTechModalOpen, setIsAssignTechModalOpen] = useState(false); 
    const { showAlert } = useModal();

    useEffect(() => { if (isOpen) setSubTicketData(initialState); }, [isOpen]);
    
    const handleMainClose = () => {
        setIsAssignTechModalOpen(false);
        onClose();
    };

    useEffect(() => {
        const handleKeyDown = (e) => { 
            if (e.key === 'Escape' && isOpen && !isAssignTechModalOpen) { 
                e.preventDefault(); 
                handleMainClose(); 
            } 
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose, isAssignTechModalOpen]);

    if (!isOpen) return null;
    const handleChange = (e) => { const { name, value } = e.target; setSubTicketData(prev => ({ ...prev, [name]: value })); };
    const handleSaveTechnicians = (technicianIds) => { setSubTicketData(prev => ({ ...prev, assignedTechnicianIds: technicianIds })); setIsAssignTechModalOpen(false); };
    const handleSubmit = (e) => { e.preventDefault(); if (!subTicketData.subject) { showAlert('Please provide a subject for the sub-ticket.', 'Validation Error'); return; } onSave(subTicketData); };
    const assignedTechniciansText = subTicketData.assignedTechnicianIds.length > 0 ? `${subTicketData.assignedTechnicianIds.length} technician(s) assigned` : 'Assign Technicians';
    const isModalVisible = !isAssignTechModalOpen;

    return (
        <>
            <AssignTechnicianModal 
                isOpen={isAssignTechModalOpen} 
                onClose={() => setIsAssignTechModalOpen(false)} 
                technicians={technicians} 
                onSave={handleSaveTechnicians} 
                currentTechnicianIds={subTicketData.assignedTechnicianIds}
            />
            <div className="modal-overlay" style={{ display: isModalVisible ? 'flex' : 'none' }}>
                <div className="modal-content">
                    <div className="modal-header">
                        <h3>Create Sub-Ticket</h3>
                        <button onClick={handleMainClose} className="modal-close-btn"><FiX /></button>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            <div className="form-group"><label>Subject</label><input type="text" name="subject" value={subTicketData.subject} onChange={handleChange} required className="form-input" autoFocus/></div>
                            <div className="form-group"><label>Description (Optional)</label><textarea name="body" value={subTicketData.body} onChange={handleChange} className="form-textarea" rows="4"></textarea></div>
                            <div className="form-group"><label>Priority</label><select name="priority" value={subTicketData.priority} onChange={handleChange} required className="form-select"><option>Low</option><option>Medium</option><option>High</option><option>Critical</option></select></div>
                            <div className="form-group"><label>Technicians</label><button type="button" className="btn btn-secondary btn-block" onClick={() => setIsAssignTechModalOpen(true)}><FiUsers className="btn-icon" />{assignedTechniciansText}</button></div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={handleMainClose}>Cancel</button>
                            <button type="submit" className="btn btn-primary">Create Sub-Ticket</button>
                        </div>
                    </form>
                </div>
            </div>
       </> 
    );
};

const SubTicketsCard = ({ parentTicketId, tickets, navigate, user, currentUserTechnicianProfile, appointments }) => {
    const liveChildTickets = useMemo(() => {
        if (!tickets || !parentTicketId) return [];
        return tickets.filter(t => {
            const pId = typeof t.parentId === 'object' ? t.parentId?._id : t.parentId;
            return pId === parentTicketId;
        }).sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    }, [tickets, parentTicketId]);

    const visibleChildTickets = useMemo(() => {
        const isAdmin = user?.roles.includes('Admin') || user?.roles.includes('Giga Admin');
        if (isAdmin) return liveChildTickets;
        if (!currentUserTechnicianProfile) return [];
        return liveChildTickets.filter(st => (st.assignedTechnicianIds || []).includes(currentUserTechnicianProfile._id));
    }, [liveChildTickets, user, currentUserTechnicianProfile]);
    
    const isOverdueChecker = useCallback((ticket) => isTicketOverdue(ticket, appointments), [appointments]);
    
    return (
        <div className="card">
            <div className="card-header"><h3 className="card-title"><FiShare2 style={{ marginRight: '0.5rem' }} />Sub-Tickets</h3></div>
            <div className="card-body" style={{ padding: '0' }}>
                <div className="ticket-list">
                    {visibleChildTickets.length > 0 ? (
                        visibleChildTickets.map(ticket => {
                            const isOverdue = isOverdueChecker(ticket);
                            const isInProgress = ticket.status === 'In Progress';
                            const itemClasses = `ticket-list-item clickable-row ${ticket.status === 'Closed' ? 'closed' : ''} ${isOverdue ? 'overdue' : ''} ${isInProgress ? 'status-in-progress-row' : ''}`;
                            return (
                                <div key={ticket._id} className={itemClasses} onClick={() => navigate(`/tickets/${ticket._id}`)}>
                                    <div className="ticket-item-info">
                                        <p className="ticket-item-title">{ticket.subject}</p>
                                        <p className="ticket-item-subtitle">Status: {ticket.status}</p>
                                    </div>
                                    <div className="ticket-item-priority"><span className={`tag priority-${ticket.priority.toLowerCase()}`}>{ticket.priority}</span></div>
                                </div>
                            );
                        })
                    ) : ( <p className="no-tasks-message">No sub-tickets assigned.</p> )}
                </div>
            </div>
        </div>
    );
};

// =================================================================================
// MAIN TICKET DETAILS COMPONENT
// =================================================================================

const TicketDetails = ({
    tickets = [], onDeleteTicket, onUpdateTicket, communities = [], technicians = [], contacts = [], inventory = [], appointments = [], onDeleteAppointment, departments = [], onAddNote, onUpdateNote, onDeleteNote, onAddInventoryToTicket, onRemoveInventoryFromTicket, ticketGroups = [], documents = [], onAddDocument, onDeleteDocument, apiCallForBlob, onCheckIn, onCheckOut, onRequestAssignment, onCreateSubTicket, onAddContact, sites = []
}) => {
    const { ticketId } = useParams();
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);
    const { showAlert, showConfirm } = useModal();
    const callApi = useApi();
    const isMobile = useIsMobile();

    const [editedTicket, setEditedTicket] = useState(null);
    const [editingField, setEditingField] = useState(null);
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
    const [isAssignContactModalOpen, setIsAssignContactModalOpen] = useState(false);
    const [isAssignClientModalOpen, setIsAssignClientModalOpen] = useState(false);
    const [isAssignGroupModalOpen, setIsAssignGroupModalOpen] = useState(false);
    const [isEditDeptsModalOpen, setIsEditDeptsModalOpen] = useState(false);
    const [isAddInventoryModalOpen, setIsAddInventoryModalOpen] = useState(false);
    const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
    const [isAddSubTicketModalOpen, setIsAddSubTicketModalOpen] = useState(false);
    
    // ✅ STATUS RADIAL MENU STATE
    const [isStatusMenuOpen, setIsStatusMenuOpen] = useState(false); 
    const [isStatusMenuPosition, setIsStatusMenuPosition] = useState({ x: 0, y: 0 }); 
    
    // ✅ PRIORITY RADIAL MENU STATE
    const [isPriorityMenuOpen, setIsPriorityMenuOpen] = useState(false);
    const [isPriorityMenuPosition, setIsPriorityMenuPosition] = useState({ x: 0, y: 0 });

    const [notification, setNotification] = useState({ isVisible: false, message: '' });
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [isCheckinConfirmOpen, setIsCheckinConfirmOpen] = useState(false);
    const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);
    const [appointmentToDelete, setAppointmentToDelete] = useState(null);
    const [isSwitchTicketModalOpen, setIsSwitchTicketModalOpen] = useState(false);
    const [conflictingTicket, setConflictingTicket] = useState(null);
    const [ticketToCheckOut, setTicketToCheckOut] = useState(null);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const [historyItem, setHistoryItem] = useState(null);
    const [isUnitHistoryModalOpen, setIsUnitHistoryModalOpen] = useState(false);
    const [historyUnitSN, setHistoryUnitSN] = useState(null);

    const canEdit = useMemo(() => user?.roles?.some(role => ['Giga Admin', 'Admin'].includes(role)), [user]);
    const ticket = useMemo(() => tickets?.find(t => t._id === ticketId), [tickets, ticketId]);
    
    const activeCheckInNames = useMemo(() => {
        if (ticket?.status !== 'In Progress' || !ticket?.activeCheckIns || ticket.activeCheckIns.length === 0) {
            return '';
        }
        return ticket.activeCheckIns.map(ci => {
            const tech = technicians.find(t => t._id === ci.technicianId);
            return tech ? tech.name : 'Unknown';
        }).join(', ');
    }, [ticket, technicians]);

    let isCheckoutImageRequired = false;

    const userDepartments = user?.departments || [];
    if (ticket && ticket.departments && Array.isArray(ticket.departments) && ticket.departments.length > 0 && departments && Array.isArray(departments)) {
        for (const ticketDeptName of ticket.departments) {
            const cleanName = String(ticketDeptName).trim().toLowerCase();
            const isUserInDept = userDepartments.some(ud => String(ud).trim().toLowerCase() === cleanName);

            if (isUserInDept) {
                const deptConfig = departments.find(d => d.name && d.name.trim().toLowerCase() === cleanName);
                if (deptConfig) {
                    if (deptConfig.requireCheckoutImage === true || deptConfig.requireCheckoutImage === "true") {
                        isCheckoutImageRequired = true;
                        break; 
                    }
                } 
                else {
                    isCheckoutImageRequired = true;
                    break;
                }
            }
        }
    }
  
    const { currentUserTechnicianProfile, isCurrentUserCheckedIn, isLastTechnician } = useMemo(() => {
        if (!user || !technicians || !ticket) return { currentUserTechnicianProfile: null, isCurrentUserCheckedIn: false, isLastTechnician: false };
        const profile = technicians.find(tech => (tech.userId?._id || tech.userId) === user.id);
        if (!profile) return { currentUserTechnicianProfile: null, isCurrentUserCheckedIn: false, isLastTechnician: false };
        const checkedIn = ticket.activeCheckIns?.some(ci => ci.technicianId.toString() === profile._id.toString()) || false;
        const lastTechnician = ticket.activeCheckIns?.length === 1 && checkedIn;
        return { currentUserTechnicianProfile: profile, isCurrentUserCheckedIn: checkedIn, isLastTechnician: lastTechnician };
    }, [user, technicians, ticket]);

    const myTruckInventory = useMemo(() => {
        if (!currentUserTechnicianProfile || !sites || !inventory) return [];
        const myTruck = sites.find(s => 
            s.type === 'Truck' && 
            s.assignedTechnician === currentUserTechnicianProfile._id
        );
        if (!myTruck) return [];
        return inventory.filter(i => i.ownerId === myTruck._id);
    }, [currentUserTechnicianProfile, sites, inventory]);

    const isLastTechnicianForCheckout = useMemo(() => {
        const target = ticketToCheckOut || ticket;
        if (!currentUserTechnicianProfile || !target) return false;
        const activeCheckIns = target.activeCheckIns || [];
        const isCheckedIn = activeCheckIns.some(ci => ci.technicianId === currentUserTechnicianProfile._id);
        return isCheckedIn && activeCheckIns.length === 1;
    }, [currentUserTechnicianProfile, ticketToCheckOut, ticket]);

    const activeGlobalTicket = useMemo(() => {
        if (!currentUserTechnicianProfile || !tickets) return null;
        return tickets.find(t => t.activeCheckIns?.some(ci => ci.technicianId === currentUserTechnicianProfile._id));
    }, [tickets, currentUserTechnicianProfile]);
    
    const isCurrentUserAssignedTechnician = useMemo(() => {
        if (!currentUserTechnicianProfile || !ticket) return false;
        return ticket.assignedTechnicianIds.includes(currentUserTechnicianProfile._id);
    }, [currentUserTechnicianProfile, ticket]);

    const isTechnician = !!currentUserTechnicianProfile;
    const hasRequestedAssignment = useMemo(() => {
        if (!isTechnician || !ticket || !ticket.assignmentRequesters) return false;
        return ticket.assignmentRequesters.some(requester => (requester._id || requester).toString() === currentUserTechnicianProfile._id.toString());
    }, [isTechnician, ticket, currentUserTechnicianProfile]);

    const showRequestAssignmentButton = useMemo(() => {
        return isTechnician && !isCurrentUserAssignedTechnician && !hasRequestedAssignment && ticket?.status !== 'Closed';
    }, [isTechnician, isCurrentUserAssignedTechnician, hasRequestedAssignment, ticket]);

    const ticketGroup = useMemo(() => {
        if (!ticket || !ticket.ticketGroupId || !ticketGroups) return null;
        return ticketGroups.find(g => g._id === ticket.ticketGroupId);
    }, [ticket, ticketGroups]);

    const availableContacts = useMemo(() => {
        if (!editedTicket?.client || !contacts || !communities) return [];
        const selectedCommunity = communities.find(c => c.name === editedTicket.client);
        if (!selectedCommunity) return [];
        return contacts.filter(contact => contact.communityId === selectedCommunity._id);
    }, [editedTicket?.client, communities, contacts]);
    
    const assignedTechnicianObjects = useMemo(() => getAssignedObjects(ticket?.assignedTechnicianIds, technicians), [ticket, technicians]);
    const assignedContactObjects = useMemo(() => getAssignedObjects(ticket?.contactIds, contacts), [ticket, contacts]);
    
    const isClient = useMemo(() => user?.roles?.includes('Client'), [user]);

    // ✅ PRIORITY OPTIONS
    const priorityOptions = useMemo(() => [
        { label: 'Critical', icon: FiAlertTriangle },
        { label: 'High', icon: FiChevronsUp },
        { label: 'Medium', icon: FiMinus },
        { label: 'Low', icon: FiChevronsDown },
    ], []);

    useEffect(() => {
        if (ticket) {
            setEditedTicket({ ...ticket });
        }
    }, [ticket]);

    const showNotification = (message) => {
        setNotification({ isVisible: true, message });
        setTimeout(() => setNotification({ isVisible: false, message: '' }), 2000);
    };

    const handleBack = () => {
        navigate('/tickets');
    };
    
    const handleDelete = () => { 
        if (canEdit) {
            showConfirm('Are you sure you want to permanently delete this ticket?', () => { onDeleteTicket(ticket._id); handleBack(); }, 'Delete Ticket', true );
        } 
    };
    
    const handleForceCheckout = (technicianId, technicianName) => {
        showConfirm(
            `Are you sure you want to force check-out for ${technicianName}? This will stop their timer and log the time accrued so far.`,
            async () => {
                const result = await callApi(`tickets/${ticket._id}/force-checkout`, 'POST', { technicianId });
                if (result.success) {
                    showNotification(`${technicianName} checked out.`);
                } else {
                    showAlert(`Error: ${result.error}`, 'Checkout Failed');
                }
            },
            'Force Checkout',
            true 
        );
    };

    const handleDoubleClick = (fieldName) => { if (canEdit) { setEditingField(fieldName); } };

    const handleInputChange = (e) => { const { name, value } = e.target;
        setEditedTicket(prev => ({ ...prev, [name]: value }));
    };
    const handleSaveInline = (fieldName) => { if (!canEdit || !editedTicket) return; const updatePayload = { [fieldName]: editedTicket[fieldName] };
    onUpdateTicket(ticket._id, updatePayload); setEditingField(null); };
    const handleKeyDown = (e, fieldName) => { if (e.key === 'Enter') { handleSaveInline(fieldName); } else if (e.key === 'Escape') { setEditedTicket({ ...ticket }); setEditingField(null); } };
    const handleSaveClientAssignment = (communityName) => { if (!canEdit) return; const updatePayload = { client: communityName, contactIds: [], }; onUpdateTicket(ticket._id, updatePayload); setIsAssignClientModalOpen(false); };
    const handleSaveTicketGroupAssignment = (groupId) => { if (!canEdit) return;
    onUpdateTicket(ticket._id, { ticketGroupId: groupId || null }); setIsAssignGroupModalOpen(false); };
    const handleSaveTechnicianAssignments = (technicianIds) => { onUpdateTicket(ticket._id, { assignedTechnicianIds: technicianIds }); setIsAssignModalOpen(false); };
    const handleSaveContactAssignments = (contactIds) => { onUpdateTicket(ticket._id, { contactIds: contactIds }); setIsAssignContactModalOpen(false); };
    const handleSaveDepartments = (departmentNames) => { onUpdateTicket(ticket._id, { departments: departmentNames }); setIsEditDeptsModalOpen(false); };
    
    // --- ✅ UPDATED: COMBINED CLICK AND LONG PRESS HANDLERS ---
    const handleStatusClick = (e) => {
        if (!canEdit) return;
        e.preventDefault(); 
        setIsStatusMenuPosition({ x: e.clientX || window.innerWidth / 2, y: e.clientY || window.innerHeight / 2 });
        setIsStatusMenuOpen(true);
    };

    const statusLongPress = useLongPress((e) => {
        if (!canEdit) return;
        const clientX = e.touches ? e.touches[0].clientX : (e.clientX || window.innerWidth / 2);
        const clientY = e.touches ? e.touches[0].clientY : (e.clientY || window.innerHeight / 2);
        
        setIsStatusMenuPosition({ x: clientX, y: clientY });
        setIsStatusMenuOpen(true);
    }, { delay: 400, shouldPreventDefault: false }); // Unblock native clicks

    const handleStatusUpdate = (newStatus) => { 
        if (canEdit && newStatus !== ticket.status) { 
            onUpdateTicket(ticket._id, { status: newStatus });
        } 
        setIsStatusMenuOpen(false); 
    };

    const handlePriorityClick = (e) => {
        if (!canEdit) return;
        e.preventDefault();
        setIsPriorityMenuPosition({ x: e.clientX || window.innerWidth / 2, y: e.clientY || window.innerHeight / 2 });
        setIsPriorityMenuOpen(true);
    };

    const priorityLongPress = useLongPress((e) => {
        if (!canEdit) return;
        const clientX = e.touches ? e.touches[0].clientX : (e.clientX || window.innerWidth / 2);
        const clientY = e.touches ? e.touches[0].clientY : (e.clientY || window.innerHeight / 2);
        
        setIsPriorityMenuPosition({ x: clientX, y: clientY });
        setIsPriorityMenuOpen(true);
    }, { delay: 400, shouldPreventDefault: false }); // Unblock native clicks

    const handlePriorityUpdate = (newPriority) => {
        if (canEdit && newPriority !== ticket.priority) {
            onUpdateTicket(ticket._id, { priority: newPriority });
        }
        setIsPriorityMenuOpen(false);
    };

    const handleCheckIn = () => { onCheckIn(ticket._id);
    setIsCheckinConfirmOpen(false);
    };
    const handlePreCheckIn = () => { 
        if (activeGlobalTicket && activeGlobalTicket._id !== ticket._id) { setConflictingTicket(activeGlobalTicket); setIsSwitchTicketModalOpen(true);
        } else { setIsCheckinConfirmOpen(true); } 
    };
    
    const handleSwitchTicket = () => { 
        if (!conflictingTicket) return; 
        setIsSwitchTicketModalOpen(false);
        setTicketToCheckOut(conflictingTicket);
        setIsCheckoutModalOpen(true);
    };
    
    const handleModalCheckOut = async (data) => {
        const targetId = ticketToCheckOut ? ticketToCheckOut._id : ticket._id;
        let checkoutPayload = { ...data };
        
        if (ticketToCheckOut) {
            checkoutPayload.newStatus = 'Pending';
            checkoutPayload.resolutionNote = `${data.resolutionNote} (Auto-switched to Ticket #${ticket._id.substring(0, 6)})`;
        }

        await onCheckOut(targetId, checkoutPayload);

        if (ticketToCheckOut) {
            await onCheckIn(ticket._id); 
            setTicketToCheckOut(null); 
            setConflictingTicket(null);
        }

        setIsCheckoutModalOpen(false);
    };

    const handleSaveNote = async (noteData) => { 
        await onAddNote(ticket._id, noteData); 
    };

    const handleRequestAssignment = async () => {
        if (!showRequestAssignmentButton) return;
        const result = await onRequestAssignment(ticket._id);
        if (result.success) { showNotification('Your assignment request has been submitted.');
        } else { showAlert(`Error: ${result.error || 'Failed to submit request.'}`, 'Request Failed'); } 
    };
    
    const handleAddItem = (itemData) => { 
        if (canEdit || isCurrentUserAssignedTechnician) {
            onAddInventoryToTicket(ticket._id, itemData); 
        }
    };

    const handleRemoveItem = (usedItem) => {
        if (canEdit) {
            showConfirm(
                `Are you sure you want to remove ${usedItem.quantityUsed}x ${usedItem.name} and return it to stock? This will erase the history of this transaction.`,
                async () => {
                    const result = await callApi(`tickets/${ticket._id}/inventory/return`, 'POST', {
                        inventoryId: usedItem.inventoryId,
                        quantity: usedItem.quantityUsed,
                        serialNumbers: usedItem.serialNumbers || []
                    });

                    if (result.success) {
                        showNotification(`${usedItem.name} returned to stock and history erased.`);
                    } else {
                        showAlert(result.error || 'Failed to return inventory.', 'Return Error');
                    }
                },
            'Return Inventory & Clear History',
                true 
            );
        }
    };

    const handleSaveSubTicket = async (subTicketData) => { if (!canEdit) return; const result = await onCreateSubTicket(ticket._id, subTicketData);
    if (result.success) { setIsAddSubTicketModalOpen(false);
    showNotification('Sub-ticket created successfully!'); } else { showAlert(`Error creating sub-ticket: ${result.error}`, 'Error'); } };

    const handleCreateContact = async (contactData) => {
        if (!ticket || !ticket.client) {
            showAlert('Cannot add contact: Ticket has no client associated.', 'Error');
            return;
        }
        const community = communities.find(c => c.name === ticket.client);
        if (!community) {
            showAlert(`Cannot find a community profile for "${ticket.client}".`, 'Error');
            return;
        }
        const result = await onAddContact({
            ...contactData,
            communityId: community._id
        });
        if (result && result.success) {
            if (result.data && result.data._id) {
                const newContactId = result.data._id;
                const updatedContactIds = [...(ticket.contactIds || []), newContactId];
                onUpdateTicket(ticket._id, { contactIds: updatedContactIds });
            }
        }
    };

    const handleInventoryItemClick = (item) => {
        const inventoryRecord = inventory.find(inv => inv._id === item.inventoryId);
        
        let queryParams = '';
        if (inventoryRecord) {
            if (inventoryRecord.ownerType === 'Site') {
                queryParams = `?siteId=${inventoryRecord.ownerId}`;
            } else if (inventoryRecord.ownerType === 'Community') {
                queryParams = `?communityId=${inventoryRecord.ownerId}`;
            }
        }

        if (item.serialNumbers && item.serialNumbers.length > 0) {
            navigate(`/inventory${queryParams}`, { state: { autoOpenUnitSerial: item.serialNumbers[0] } });
        } else {
            navigate(`/inventory${queryParams}`, { state: { showHistory: item.inventoryId } });
        }
    };

    const handleSubTicketModalClose = () => setIsAddSubTicketModalOpen(false);

    if (!ticket || !editedTicket) {
        return ( <div className="page-container"><div className="page-header"><div className="header-left-details"><button onClick={handleBack} className="back-btn" title="Go Back"><FiArrowLeft /></button><h2 className="page-title">Ticket Not Found</h2></div></div><div className="card"><p>The ticket you are looking for does not exist or has been deleted.</p></div></div> );
    }

    if (isMobile) {
        const showCheckIn = isCurrentUserAssignedTechnician && !isCurrentUserCheckedIn && ticket.status !== 'Closed';
        const showCheckOut = isCurrentUserAssignedTechnician && isCurrentUserCheckedIn;
        const showRequest = showRequestAssignmentButton;
        const showAddNote = !isClient && (canEdit || isCurrentUserAssignedTechnician);
        const hasActionBarContents = showCheckIn || showCheckOut || showRequest || showAddNote;

        return (
            <div className="page-container mobile-ticket-details-page" style={{ paddingBottom: hasActionBarContents ? '180px' : '100px' }} key={ticketId}>
                
                {/* --- STANDARD HEADER --- */}
                <div className="page-header mobile-details-header" style={{ marginBottom: '1.5rem' }}>
                    <div className="header-left-details" style={{ width: '100%', alignItems: 'flex-start' }}>
                        <button type="button" onClick={handleBack} className="back-btn" style={{ marginTop: '4px' }} title="Go Back"><FiArrowLeft /></button>
                        <div className="mobile-header-text">
                            <h2 className="page-title mobile-header-title">{ticket.subject}</h2>
                            <span className="page-subtitle mobile-header-subtitle">{ticket.client}</span>
                        </div>
                    </div>
                </div>

                <div className="details-main-column mobile-details-scroll-content">
                    
                    {/* CORE DETAILS CARD */}
                    <div className="card details-card mobile-detail-card">
                        <div className="details-grid" style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                            
                            <div className="detail-item mobile-card-section">
                                <span className="detail-label mobile-label">Status</span>
                                <div className="detail-value mobile-value interactive-status-wrapper">
                                    <span 
                                        className={`status-badge status-${ticket.status.toLowerCase().replace(/ /g, '-')}` + (canEdit ? ' clickable' : '')} 
                                        {...statusLongPress} 
                                        onClick={handleStatusClick}
                                        title={ticket.status === 'In Progress' && activeCheckInNames ? `Checked in: ${activeCheckInNames}` : (canEdit ? "Tap or long-press to change status" : "")}
                                    >
                                        {ticket.status}
                                    </span>
                                    <TicketTimeTracker ticket={ticket} />
                                </div>
                            </div>

                            <div className="detail-item mobile-card-section">
                                <span className="detail-label mobile-label">Priority</span>
                                <div className="detail-value mobile-value interactive-status-wrapper">
                                    <span 
                                        className={`tag priority-${(ticket.priority || 'low').toLowerCase()} ${canEdit ? 'clickable' : ''}`} 
                                        {...priorityLongPress} 
                                        onClick={handlePriorityClick}
                                        title={canEdit ? "Tap or long-press to change priority" : ""}
                                    >
                                        {ticket.priority || 'N/A'}
                                    </span>
                                </div>
                            </div>

                            <div className="detail-item mobile-card-section">
                                <span className="detail-label mobile-label">Assigned To</span>
                                <div className={`detail-value mobile-value tag-group ${canEdit ? 'editable-field' : ''}`} onClick={() => canEdit && setIsAssignModalOpen(true)}>
                                    {assignedTechnicianObjects.length > 0
                                        ? assignedTechnicianObjects.map(t => <span key={t._id} className="detail-tag">{t.name}</span>)
                                        : <span style={{ color: 'var(--gray-400)', fontStyle: 'italic' }}>Unassigned</span>
                                    }
                                </div>
                            </div>

                            <div className="detail-item mobile-card-section">
                                <span className="detail-label mobile-label">Description</span>
                                <div className="detail-value mobile-value" style={{ display: 'block', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: '1.5' }}>
                                    {ticket.body || <span style={{ fontStyle: 'italic', color: 'var(--gray-400)' }}>No description provided.</span>}
                                </div>
                            </div>

                            <div className="detail-item mobile-card-section">
                                <span className="detail-label mobile-label">Location</span>
                                <div className="detail-value mobile-value" onClick={() => window.open(`http://googleusercontent.com/maps.google.com/?q=${ticket.client}`, '_blank')}>
                                    <FiMapPin size={16} style={{ color: 'var(--blue-600)', marginRight: '4px' }} />
                                    <span style={{ textDecoration: 'underline', color: 'var(--blue-600)' }}>{ticket.client}</span>
                                </div>
                            </div>

                        </div>
                    </div>

                    {/* SUB-TICKETS CARD */}
                    {!isClient && !ticket.parentId && (
                        <div className="mobile-detail-card">
                            <SubTicketsCard parentTicketId={ticket._id} tickets={tickets} navigate={navigate} user={user} currentUserTechnicianProfile={currentUserTechnicianProfile} appointments={appointments} />
                        </div>
                    )}

                    {/* PARTS USED CARD */}
                    {!isClient && (
                        <div className="card mobile-detail-card">
                            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h3 className="card-title"><FiPackage style={{ marginRight: '0.5rem' }} />Used Inventory</h3>
                                {(canEdit || isCurrentUserAssignedTechnician) && (
                                    <button className="btn btn-secondary btn-sm" onClick={() => setIsAddInventoryModalOpen(true)}><FiPlus className="btn-icon" /> Add</button>
                                )}
                            </div>
                            <div className="card-body" style={{ padding: '0' }}>
                                <div className="items-list">
                                    {ticket.usedInventory && ticket.usedInventory.length > 0 ? (
                                        ticket.usedInventory.map((item, index) => (
                                            <div key={index} className="list-item-row clickable-row mobile-card-section" onClick={(e) => { e.stopPropagation(); handleInventoryItemClick(item); }}>
                                                <div className="item-info">
                                                    <span className="item-title">{item.name}</span>
                                                    <span className="item-subtitle">Qty: {item.quantityUsed} • {item.sku}</span>
                                                    {item.serialNumbers && item.serialNumbers.length > 0 && (
                                                        <div style={{ marginTop: '0.25rem', fontSize: '0.8rem', color: 'var(--blue-600)', display:'flex', flexWrap:'wrap', gap:'4px' }}>
                                                            {item.serialNumbers.map((sn, i) => (
                                                                <span key={i} style={{background:'var(--blue-50)', padding:'2px 6px', borderRadius:'4px', border:'1px solid var(--blue-200)', display:'flex', alignItems:'center'}}>
                                                                    <span style={{fontWeight:'bold', marginRight:'3px'}}>SN:</span> {sn}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                                {canEdit && (
                                                    <div className="item-actions">
                                                        <button onClick={(e) => { e.stopPropagation(); handleRemoveItem(item); }} className="btn-icon-only btn-icon-danger"><FiTrash2 size={16} /></button>
                                                    </div>
                                                )}
                                            </div>
                                        ))
                                    ) : (
                                        <p className="no-tasks-message mobile-card-section">No items used.</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* NOTES CARD */}
                    {!isClient && (
                        <div className="mobile-detail-card">
                            <TicketNotes ticketId={ticket._id} notes={ticket.notes} onAddNoteClick={() => setIsAddNoteModalOpen(true)} onUpdateNote={onUpdateNote} onDeleteNote={onDeleteNote} apiCallForBlob={apiCallForBlob} canEdit={canEdit} />
                        </div>
                    )}

                </div>

                {/* --- FLOATING ACTION BAR --- */}
                {hasActionBarContents && (
                    <div className="mobile-action-bar" style={{ position: 'fixed', bottom: 0, left: 0, right: 0, backgroundColor: 'var(--mobile-glass-bg)', padding: '0.75rem 1rem', paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))', borderTop: '1px solid var(--gray-200)', display: 'flex', gap: '0.75rem', zIndex: 1020, boxShadow: '0 -4px 12px rgba(0,0,0,0.05)' }}>
                        {showCheckIn && (
                            <button onClick={handlePreCheckIn} className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }}>
                                <FiLogIn className="btn-icon" /> Check In
                            </button>
                        )}
                        {showCheckOut && (
                            <button onClick={() => setIsCheckoutModalOpen(true)} className="btn btn-success" style={{ flex: 1, justifyContent: 'center' }}>
                                <FiLogOut className="btn-icon" /> Check Out
                            </button>
                        )}
                        {showRequest && (
                            <button onClick={handleRequestAssignment} className="btn btn-secondary" style={{ flex: 1, justifyContent: 'center' }}>
                                <FiUserPlus className="btn-icon" /> Request
                            </button>
                        )}
                        {showAddNote && (
                            <button onClick={() => setIsAddNoteModalOpen(true)} className="btn btn-secondary" style={{ padding: '0.5rem 1rem' }}>
                                <FiMessageSquare size={18} />
                            </button>
                        )}
                    </div>
                )}

                {/* --- Hidden Modals (Keep these mounted) --- */}
                {isStatusMenuOpen && (
                    <RadialStatusMenu
                        onSelect={handleStatusUpdate}
                        onClose={() => setIsStatusMenuOpen(false)}
                        clickPosition={isStatusMenuPosition}
                    />
                )}
                {isPriorityMenuOpen && (
                    <RadialStatusMenu
                        onSelect={handlePriorityUpdate}
                        onClose={() => setIsPriorityMenuOpen(false)}
                        clickPosition={isPriorityMenuPosition}
                        options={priorityOptions}
                    />
                )}
                <ConfirmationModal isOpen={isConfirmModalOpen} onClose={() => setIsConfirmModalOpen(false)} onConfirm={() => { }} title="Delete Appointment" message={`Are you sure you want to delete the appointment for "${appointmentToDelete?.title}"?`} confirmText="Delete" />
                <ConfirmationModal isOpen={isCheckinConfirmOpen} onClose={() => setIsCheckinConfirmOpen(false)} onConfirm={handleCheckIn} title="Confirm Check-In" message="Are you sure you want to check into this ticket?" confirmText="Check In" />
                <ConfirmationModal isOpen={isSwitchTicketModalOpen} onClose={() => setIsSwitchTicketModalOpen(false)} onConfirm={handleSwitchTicket} title="Switch Active Ticket" message={`You are checked into "${conflictingTicket?.subject}". Switch?`} confirmText="Switch" />

                <InventoryHistoryModal isOpen={isHistoryOpen} onClose={() => setIsHistoryOpen(false)} item={historyItem} onManageStock={canEdit ? (item) => handleRemoveItem(item) : null} />
                <UnitHistoryModal isOpen={isUnitHistoryModalOpen} onClose={() => setIsUnitHistoryModalOpen(false)} serialNumber={historyUnitSN} />

                <CheckOutModal key={isCheckoutImageRequired ? 'image-required' : 'image-optional'} isOpen={isCheckoutModalOpen} onClose={() => { setIsCheckoutModalOpen(false); setTicketToCheckOut(null); }} onCheckOut={handleModalCheckOut} isLastTechnician={isLastTechnicianForCheckout} isImageRequired={isCheckoutImageRequired} truckInventory={myTruckInventory} />

                <AssignTechnicianModal isOpen={isAssignModalOpen} onClose={() => setIsAssignModalOpen(false)} technicians={technicians} onSave={handleSaveTechnicianAssignments} currentTechnicianIds={ticket.assignedTechnicianIds} />
                <AssignContactsModal isOpen={isAssignContactModalOpen} onClose={() => setIsAssignContactModalOpen(false)} contacts={availableContacts} onSave={handleSaveContactAssignments} currentContactIds={ticket.contactIds} onCreate={handleCreateContact} />
                <AssignDepartmentsModal isOpen={isEditDeptsModalOpen} onClose={() => setIsEditDeptsModalOpen(false)} onSave={handleSaveDepartments} allDepartments={departments} currentDepartments={ticket.departments} />
                <AssignClientModal isOpen={isAssignClientModalOpen} onClose={() => setIsAssignClientModalOpen(false)} onSave={handleSaveClientAssignment} communities={communities} currentClientName={ticket.client} />

                <AddInventoryModal isOpen={isAddInventoryModalOpen} onClose={() => setIsAddInventoryModalOpen(false)} truckInventory={myTruckInventory} onAddItem={handleAddItem} />
                <AddNoteModal isOpen={isAddNoteModalOpen} onClose={() => setIsAddNoteModalOpen(false)} onSave={handleSaveNote} />
                <AddSubTicketModal isOpen={isAddSubTicketModalOpen} onClose={handleSubTicketModalClose} onSave={onCreateSubTicket} technicians={technicians} />
            </div>
        );
    }

    // --- DESKTOP RENDER ---
    return (
        <div className="page-container" key={ticketId} style={{ paddingBottom: '100px' }}>
            {/* Radial Menus */}
            {isStatusMenuOpen && <RadialStatusMenu onSelect={handleStatusUpdate} onClose={() => setIsStatusMenuOpen(false)} clickPosition={isStatusMenuPosition} />}
            {isPriorityMenuOpen && <RadialStatusMenu onSelect={handlePriorityUpdate} onClose={() => setIsPriorityMenuOpen(false)} clickPosition={isPriorityMenuPosition} options={priorityOptions} />}

            {/* Confirmations & History Modals */}
            <ConfirmationModal isOpen={isConfirmModalOpen} onClose={() => setIsConfirmModalOpen(false)} onConfirm={() => {}} title="Delete Appointment" message={`Are you sure you want to delete the appointment for "${appointmentToDelete?.title}"?`} confirmText="Delete" />
            <ConfirmationModal isOpen={isCheckinConfirmOpen} onClose={() => setIsCheckinConfirmOpen(false)} onConfirm={handleCheckIn} title="Confirm Check-In" message="Are you sure you want to check into this ticket?" confirmText="Check In" />
            <ConfirmationModal isOpen={isSwitchTicketModalOpen} onClose={() => setIsSwitchTicketModalOpen(false)} onConfirm={handleSwitchTicket} title="Switch Active Ticket" message={`You are checked into "${conflictingTicket?.subject}". Switch?`} confirmText="Switch" />
            <InventoryHistoryModal isOpen={isHistoryOpen} onClose={() => setIsHistoryOpen(false)} item={historyItem} onManageStock={canEdit ? (item) => handleRemoveItem(item) : null} />
            <UnitHistoryModal isOpen={isUnitHistoryModalOpen} onClose={() => setIsUnitHistoryModalOpen(false)} serialNumber={historyUnitSN} />
            <CheckOutModal key={isCheckoutImageRequired ? 'image-required' : 'image-optional'} isOpen={isCheckoutModalOpen} onClose={() => { setIsCheckoutModalOpen(false); setTicketToCheckOut(null); }} onCheckOut={handleModalCheckOut} isLastTechnician={isLastTechnicianForCheckout} isImageRequired={isCheckoutImageRequired} truckInventory={myTruckInventory} />
            
            {/* Management Modals */}
            <AssignTechnicianModal isOpen={isAssignModalOpen} onClose={() => setIsAssignModalOpen(false)} technicians={technicians} onSave={handleSaveTechnicianAssignments} currentTechnicianIds={ticket.assignedTechnicianIds} />
            <AssignContactsModal isOpen={isAssignContactModalOpen} onClose={() => setIsAssignContactModalOpen(false)} contacts={availableContacts} onSave={handleSaveContactAssignments} currentContactIds={ticket.contactIds} onCreate={handleCreateContact} />
            <AssignDepartmentsModal isOpen={isEditDeptsModalOpen} onClose={() => setIsEditDeptsModalOpen(false)} onSave={handleSaveDepartments} allDepartments={departments} currentDepartments={ticket.departments} />
            <AssignClientModal isOpen={isAssignClientModalOpen} onClose={() => setIsAssignClientModalOpen(false)} onSave={handleSaveClientAssignment} communities={communities} currentClientName={ticket.client} />
            <AssignTicketGroupModal isOpen={isAssignGroupModalOpen} onClose={() => setIsAssignGroupModalOpen(false)} onSave={handleSaveTicketGroupAssignment} ticketGroups={ticketGroups} currentTicketGroupId={ticket.ticketGroupId} />
            <AddInventoryModal isOpen={isAddInventoryModalOpen} onClose={() => setIsAddInventoryModalOpen(false)} truckInventory={myTruckInventory} onAddItem={handleAddItem} />
            <AddNoteModal isOpen={isAddNoteModalOpen} onClose={() => setIsAddNoteModalOpen(false)} onSave={handleSaveNote} />
            <AddSubTicketModal isOpen={isAddSubTicketModalOpen} onClose={handleSubTicketModalClose} onSave={onCreateSubTicket} technicians={technicians} />
            <ClipboardNotification message={notification.message} isVisible={notification.isVisible} />

            {/* Header */}
            <div className="page-header">
                <div className="header-left-details">
                    <button type="button" onClick={handleBack} className="back-btn" title="Go Back"><FiArrowLeft /></button>
                    <h2 className="page-title" title={ticket.subject}>{ticket.subject}</h2>
                </div>
                <div className="header-actions">
                    {isCurrentUserAssignedTechnician && !isCurrentUserCheckedIn && ticket.status !== 'Closed' && (
                        <button onClick={handlePreCheckIn} className="btn btn-primary"><FiLogIn className="btn-icon" /> Check In</button>
                    )}
                    {isCurrentUserAssignedTechnician && isCurrentUserCheckedIn && (
                        <button onClick={() => setIsCheckoutModalOpen(true)} className="btn btn-success"><FiLogOut className="btn-icon" /> Check Out</button>
                    )}
                    {showRequestAssignmentButton && (
                        <button onClick={handleRequestAssignment} className="btn btn-secondary"><FiUserPlus className="btn-icon" /> Request Assignment</button>
                    )}
                    {canEdit && (
                        <>
                            {!ticket.parentId && ticket.status !== 'Closed' && (
                                <button onClick={() => setIsAddSubTicketModalOpen(true)} className="btn btn-secondary" title="Add Sub-Ticket"><FiShare2 className="btn-icon" /> Add Sub-Ticket</button>
                            )}
                            <button onClick={handleDelete} className="btn btn-danger" title="Delete Ticket"><FiTrash2 className="btn-icon" /> Delete</button>
                        </>
                    )}
                </div>
            </div>

            <div className="details-page-grid">
                <div className="details-main-column">
                    <div className="details-card">
                        <div className="details-grid">
                            <div className="detail-item full-width">
                                {ticket.parentId && (<div className="parent-ticket-link-container" style={{ marginBottom: '1rem' }}><span className="detail-label">Parent Ticket</span><button className="btn-link" onClick={() => navigate(`/tickets/${ticket.parentId._id}`)}><FiArrowLeft style={{ marginRight: '0.25rem' }} />{ticket.parentId.subject}</button></div>)}
                            </div>
                            <div className="detail-item">
                                <span className="detail-label">Ticket ID</span>
                                <div className="detail-value" onClick={() => { navigator.clipboard.writeText(ticket._id); showNotification('Ticket ID copied!'); }} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }} title="Click to copy"><span style={{ fontFamily: 'monospace' }}>{ticket._id}</span><FiCopy size={14} style={{ color: 'var(--gray-5)' }}/></div>
                            </div>
    
                            <div className="detail-item"><span className="detail-label">Client</span><span className={`detail-value ${canEdit ? 'editable-field' : ''}`} onClick={() => { if (canEdit) setIsAssignClientModalOpen(true); }}>{ticket.client || 'N/A'}</span></div>
                            <div className="detail-item"><span className="detail-label">Contact(s)</span><div className={`detail-value tag-group ${canEdit ? 'editable-field' : ''}`} onClick={() => canEdit && setIsAssignContactModalOpen(true)}>{assignedContactObjects.length > 0 ? ( assignedContactObjects.map(contact => <InfoTagWithTooltip key={contact._id} name={contact.name} tooltipItems={[{ label: 'Email', value: contact.email }, { label: 'Phone', value: formatPhoneNumber(contact.phone) }]} onCopy={(label) => showNotification(`${label} copied!`)} />) ) : ( 'Unassigned' )}</div></div>
                            
                            <div className="detail-item"><span className="detail-label">Departments</span><div className={`detail-value tag-group ${canEdit ? 'editable-field' : ''}`} onClick={() => canEdit && setIsEditDeptsModalOpen(true)}>{(ticket.departments && ticket.departments.length > 0) ? ticket.departments.map(dept => <span key={dept} className="detail-tag">{dept}</span>) : 'Unassigned'}</div></div>
                            <div className="detail-item">
                                <span className="detail-label">Assigned To</span>
                                <div className={`detail-value tag-group ${canEdit ? 'editable-field' : ''}`} onClick={() => canEdit && setIsAssignModalOpen(true)}>
                                    {assignedTechnicianObjects.length > 0 ? (
                                        assignedTechnicianObjects.map(tech => {
                                            const isCheckedIn = ticket.activeCheckIns?.some(ci => ci.technicianId === tech._id);
                                            return (
                                            <div key={tech._id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                <InfoTagWithTooltip name={tech.name} tooltipItems={[{ label: 'Email', value: tech.email }, { label: 'Phone', value: formatPhoneNumber(tech.phone) }]} onCopy={(label) => showNotification(`${label} copied!`)} position="right" />
                                                <TechnicianTimeLog ticket={ticket} technicianId={tech._id} />
                                                {canEdit && isCheckedIn && (
                                                    <button className="btn-icon-only btn-icon-danger" title={`Force Check-Out ${tech.name}`} onClick={(e) => { e.stopPropagation(); handleForceCheckout(tech._id, tech.name); }} style={{ marginLeft: '0.25rem', padding: '2px' }}><FiUserX size={14} /></button>
                                                )}
                                            </div>
                                        );})
                                    ) : ( 'Unassigned' )}
                                </div>{canEdit && ticket.assignmentRequesters && ticket.assignmentRequesters.length > 0 && ( <div className="assignment-request-box"><span className="detail-label"><FiUserPlus size={12} style={{ marginRight: '0.25rem' }}/>Assignment Requests</span><div className="detail-value tag-group" style={{ marginTop: '0.5rem' }}>{ticket.assignmentRequesters.map(requester => ( <span key={requester._id || requester} className="detail-tag" style={{ cursor: 'default' }}>{requester.name}</span> ))}</div></div> )}</div>
                            
                            <div className="detail-item">
                                <span className="detail-label">Status</span>
                                <div className="detail-value interactive-status-wrapper">
                                    {/* ✅ COMBINED CLICK AND LONG PRESS */}
                                    <span 
                                        className={`status-badge status-${ticket.status.toLowerCase().replace(/ /g, '-')}` + (canEdit ? ' clickable' : '')} 
                                        {...statusLongPress} 
                                        onClick={handleStatusClick}
                                        title={ticket.status === 'In Progress' && activeCheckInNames ? `Checked in: ${activeCheckInNames}` : (canEdit ? "Tap or long-press to change status" : "")}
                                    >
                                        {ticket.status}
                                    </span>
                                    <TicketTimeTracker ticket={ticket} />
                                </div>
                            </div>
    
                            <div className="detail-item"><span className="detail-label">Created On</span><span className="detail-value">{new Date(ticket.createdAt).toLocaleString()}</span></div>
                            
                            {!isClient && (
                                <div className="detail-item">
                                    <span className="detail-label">Priority</span>
                                    <div className="detail-value interactive-status-wrapper">
                                        {/* ✅ COMBINED CLICK AND LONG PRESS */}
                                        <span 
                                            className={`tag priority-${(ticket.priority || 'low').toLowerCase()} ${canEdit ? 'clickable' : ''}`} 
                                            {...priorityLongPress} 
                                            onClick={handlePriorityClick}
                                            title={canEdit ? "Tap or long-press to change priority" : ""}
                                        >
                                            {ticket.priority || 'N/A'}
                                        </span>
                                    </div>
                                </div>
                            )}
                            <div className="detail-item full-width"><span className="detail-label">Full Subject</span>{editingField === 'subject' && canEdit ? ( <input type="text" name="subject" value={editedTicket.subject} onChange={handleInputChange} onBlur={() => handleSaveInline('subject')} onKeyDown={(e) => handleKeyDown(e, 'subject')} className="form-input" autoFocus /> ) : ( <span className={`detail-value ${canEdit ? 'editable-field' : ''}`} onClick={() => handleDoubleClick('subject')}>{ticket.subject}</span> )}</div>
                            <div className="detail-item full-width"><span className="detail-label">Body</span>{editingField === 'body' && canEdit ? ( <textarea name="body" value={editedTicket.body} onChange={handleInputChange} onBlur={() => handleSaveInline('body')} className="form-textarea" rows="5" autoFocus /> ) : ( <p className={`detail-description ${canEdit ? 'editable-field' : ''}`} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }} onClick={() => handleDoubleClick('body')}>{ticket.body || 'No detailed body was provided for this ticket.'}</p> )}</div>
                        </div>
                    </div>
                </div>
                <div className="details-side-column">
                    {!isClient && !ticket.parentId && (
                        <SubTicketsCard 
                            parentTicketId={ticket._id} 
                            tickets={tickets} 
                            navigate={navigate} 
                            user={user} 
                            currentUserTechnicianProfile={currentUserTechnicianProfile} 
                            appointments={appointments} 
                        />
                    )}
    
                    {!isClient && (
                        <div className="card">
                            <div className="card-header"><h3 className="card-title"><FiPackage style={{ marginRight: '0.5rem' }} />Used Inventory</h3></div>
                            <div className="card-body" style={{ padding: '0', display: 'flex', flexDirection: 'column' }}>
                                <div className="items-list">
                                    {ticket.usedInventory && ticket.usedInventory.length > 0 ? ( 
                                        ticket.usedInventory.map((item, index) => ( 
                                            <div 
                                        key={index} 
                                        className="list-item-row clickable-row" 
                                        onClick={(e) => { e.stopPropagation(); handleInventoryItemClick(item); }} 
                                        title="Click to view history"
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <div className="item-info">
                                            <span className="item-title">{item.name}</span>
                                            <span className="item-subtitle">Qty: {item.quantityUsed} • {item.sku}</span>
                                            
                                            {item.serialNumbers && item.serialNumbers.length > 0 && (
                                                <div style={{ marginTop: '0.25rem', fontSize: '0.8rem', color: 'var(--blue-600)', display:'flex', flexWrap:'wrap', gap:'4px' }}>
                                                    {item.serialNumbers.map((sn, i) => (
                                                                <span key={i} style={{background:'var(--blue-50)', padding:'2px 6px', borderRadius:'4px', border:'1px solid var(--blue-200)', display:'flex', alignItems:'center'}}>
                                                                    <span style={{fontWeight:'bold', marginRight:'3px'}}>SN:</span> {sn}
                                                                </span>
                                                            ))}
                                                        </div>
                                            )}
                                        </div>
                                    {canEdit && ( 
                                            <div className="item-actions">
                                                <button onClick={(e) => { e.stopPropagation(); handleRemoveItem(item); }} className="btn-icon-only btn-icon-danger"><FiTrash2 size={16} /></button>
                                            </div> 
                                        )}
                                </div> 
                                    )) ) : ( <p className="no-tasks-message">No items used.</p> )}
                                </div>
                            </div>
                        </div>
                    )}

                    {!isClient && <TicketNotes ticketId={ticket._id} notes={ticket.notes} onAddNoteClick={() => setIsAddNoteModalOpen(true)} onUpdateNote={onUpdateNote} onDeleteNote={onDeleteNote} apiCallForBlob={apiCallForBlob} canEdit={canEdit} />}
                </div>
            </div>
        </div>
    );
};

export default TicketDetails;